import { sectionJudgeIds } from "score-common";

// card #761 — section-judge gating: a booth may only ENTER the sections
// assigned to its judge via Class.sectionJudgeAssignments (score-judge
// roster ids, #759); every other section renders read-only until the
// assigned booth's own sheet for that section is submitted.
//
// The bridge problem this module solves: Class.judgeAssignments (and its
// scribe mirror) are keyed by arena POSITION (C/E/H/M/B) and store a judge
// NAME string — see apps/score-api/src/handlers/daysheet.js's
// mergeJudgeAssignments/judgeNameKey, which is authoritative for this shape.
// Class.sectionJudgeAssignments instead stores a score-judge roster ID
// (GET /judge, card #730's JUDGE table). Nothing persists a position->id
// mapping directly, so this resolves it the same way score-api's reconcile
// does when it builds Class.judgeIds: match the position's judge NAME
// against the show's judge roster (firstName + " " + lastName).

// #542's qualityJudging predates this and only ever gates by POSITION
// (technical hardcoded to C, quality to qualityJudging.qualityPosition).
// sectionJudgeAssignments is a separate, judge-IDENTITY-based mechanism
// (#759) — the two are independent; this module only concerns itself with
// sectionJudgeAssignments.

function judgeNameKey(judge) {
  return [judge?.firstName, judge?.lastName].filter(Boolean).join(" ").trim();
}

// #2094: FVD-sourced names drift from the roster (casing, trailing/double
// spaces, punctuation, diacritics, a middle initial). Compare on a
// normalized token list instead of raw string equality.
function nameTokens(text) {
  return String(text ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, " ")
    .split(/\s+/)
    .filter(Boolean);
}

// Resolve the roster row for a stored judge `name`. Tiers: (1) the exact
// string, (2) every normalized token equal, (3) same first and last token
// (a middle name/initial on either side), accepted ONLY when exactly one
// roster row matches. Ambiguous or no match returns null, which keeps the
// section locked, because handing it to the wrong booth lets two judges race
// to score the same section.
function matchRosterJudge(judgeRoster, name) {
  const exact = judgeRoster.find((j) => judgeNameKey(j) === name);
  if (exact) return exact;
  const want = nameTokens(name);
  if (want.length === 0) return null;
  const full = judgeRoster.filter((j) => {
    const have = nameTokens(judgeNameKey(j));
    return have.length === want.length && have.every((t, i) => t === want[i]);
  });
  if (full.length === 1) return full[0];
  if (full.length > 1 || want.length < 2) return null;
  const ends = judgeRoster.filter((j) => {
    const have = nameTokens(judgeNameKey(j));
    return (
      have.length >= 2 &&
      have[0] === want[0] &&
      have[have.length - 1] === want[want.length - 1]
    );
  });
  return ends.length === 1 ? ends[0] : null;
}

// Resolve the score-judge roster id sitting at `position` for this class, by
// matching Class.judgeAssignments[position]'s name against the show's judge
// roster. Returns null if unresolvable (no name assigned, or no matching
// roster row — e.g. the roster hasn't loaded yet, or offline with a stale
// cache).
export function resolveJudgeIdAtPosition(klass, judgeRoster, position) {
  const name = klass?.judgeAssignments?.[position]?.[0];
  if (!name || !judgeRoster?.length) return null;
  return matchRosterJudge(judgeRoster, name)?.id ?? null;
}

// The only two movement `section` values sectionJudgeAssignments can gate.
// Any other section (undefined, or freestyle's "artistic") is never gated
// by this field — freestyle's technical/artistic split is untouched by #761.
const GATED_SECTIONS = new Set(["technical", "quality"]);

export function hasSectionJudgeAssignments(klass) {
  const a = klass?.sectionJudgeAssignments;
  return Boolean(a && (sectionJudgeIds(a, "technical").length || sectionJudgeIds(a, "quality").length));
}

/**
 * Is `section` (a movement's `section` field) editable by the scribe/judge
 * sitting at `position` for this class?
 *
 *  - Class has no sectionJudgeAssignments at all (neither key set) → true,
 *    UNCHANGED — every class that predates #759/#761 keeps working exactly
 *    as before (whatever #542's qualityJudging/visibleMovementsFor already
 *    decided is untouched).
 *  - `section` isn't one sectionJudgeAssignments covers → true, never
 *    gated here.
 *  - That section's assignment is unset (null) on a class that IS
 *    section-gated (the other section is assigned) → false for EVERY booth
 *    (#798). Nobody has been declared responsible for it, and handing it to
 *    all booths lets two judges race to score the same section. The admin
 *    assigns both sections (#760 drawer) to unlock it; scribe is a kiosk, so
 *    there is deliberately no override here.
 *  - Otherwise: true iff the assigned score-judge id resolves to THIS
 *    booth's position.
 */
export function isSectionEditableForBooth({ klass, judgeRoster, position, section }) {
  if (!hasSectionJudgeAssignments(klass)) return true;
  if (!GATED_SECTIONS.has(section)) return true;
  // #3372: a section can seat several judges; any of them may enter it.
  const assignedJudgeIds = sectionJudgeIds(klass.sectionJudgeAssignments, section);
  if (assignedJudgeIds.length === 0) return false;
  const myJudgeId = resolveJudgeIdAtPosition(klass, judgeRoster, position);
  return Boolean(myJudgeId) && assignedJudgeIds.includes(myJudgeId);
}

/**
 * The arena position (C/E/H/M/B) whose judge is assigned `section`, if
 * resolvable — used to (a) label a locked section ("awaiting the judge at
 * E") and (b) look up that booth's own ScoreSheet so a locked section can
 * show its submitted values once they exist (#761 AC2), instead of staying
 * blank forever.
 */
export function resolvePositionForSection({ klass, judgeRoster, section }) {
  if (!GATED_SECTIONS.has(section)) return null;
  const assignedJudgeIds = sectionJudgeIds(klass?.sectionJudgeAssignments, section);
  if (assignedJudgeIds.length === 0 || !judgeRoster?.length) return null;
  const assignments = klass?.judgeAssignments ?? {};
  for (const position of Object.keys(assignments)) {
    if (assignedJudgeIds.includes(resolveJudgeIdAtPosition(klass, judgeRoster, position))) {
      return position;
    }
  }
  return null;
}
